import { WorkflowAction, WorkflowStep } from "@/types/breeze/Workflow";

import { WorkflowEvent } from "@/types/breeze/Workflow";

import { WorkflowTriggerType } from "@/types/breeze/Workflow";

const STEP_PROJECTION_PATTERN = /\$anythink\.steps\.([a-zA-Z0-9_]+)\.data\[\*\]/;

function findStepProjectionKey(value: unknown): string | null {
  if (typeof value === "string") {
    const match = value.match(STEP_PROJECTION_PATTERN);
    return match ? match[1] : null;
  }
  if (Array.isArray(value)) {
    for (const v of value) {
      const k = findStepProjectionKey(v);
      if (k) return k;
    }
    return null;
  }
  if (value && typeof value === "object") {
    for (const v of Object.values(value)) {
      const k = findStepProjectionKey(v);
      if (k) return k;
    }
    return null;
  }
  return null;
}

const getStepLoopDescription = (step: WorkflowStep): string | null => {
  const p = (step.parameters ?? {}) as Record<string, unknown>;

  if (step.action === WorkflowAction.CreateData || step.action === WorkflowAction.UpsertData) {
    const loopOver = typeof p.loop_over === "string" ? p.loop_over.trim() : "";
    return loopOver ? "Loops once per item in the source array" : null;
  }

  if (step.action !== WorkflowAction.SendPushNotification) return null;
  const audience = (p.audience ?? {}) as Record<string, unknown>;

  if (audience.kind === "users") return "Loops once per user in the recipient list";

  const candidates: unknown[] = [
    p.title,
    p.body,
    p.image_url,
    p.click_action,
    p.category,
    p.data,
    p.buttons,
    audience.user_id,
    audience.owner_entity_id,
  ];
  for (const c of candidates) {
    const key = findStepProjectionKey(c);
    if (key) return `Loops once per row of ${key}`;
  }
  return null;
};

const getTriggerLabel = (trigger: WorkflowTriggerType) => {
  switch (trigger) {
    case WorkflowTriggerType.Api:
      return "API";
    case WorkflowTriggerType.Event:
      return "Event";
    case WorkflowTriggerType.Timed:
      return "Timed";
    case WorkflowTriggerType.Manual:
      return "Manual";
    default:
      return "Unknown";
  }
};

const getEventLabel = (event: WorkflowEvent) => {
  switch (event) {
    case WorkflowEvent.EntityCreated:
      return "Entity Created";
    case WorkflowEvent.EntityUpdated:
      return "Entity Updated";
    case WorkflowEvent.EntityDeleted:
      return "Entity Deleted";
    case WorkflowEvent.UserRegistered:
      return "User Registered";
    case WorkflowEvent.UserInvited:
      return "User Invited";
    case WorkflowEvent.SubscriptionCreated:
      return "Subscription Created";
    case WorkflowEvent.SubscriptionActivated:
      return "Subscription Activated";
    case WorkflowEvent.SubscriptionExpired:
      return "Subscription Expired";
    case WorkflowEvent.SubscriptionCancelled:
      return "Subscription Cancelled";
    case WorkflowEvent.PaymentSucceeded:
      return "Payment Succeeded";
    case WorkflowEvent.PaymentFailed:
      return "Payment Failed";
    case WorkflowEvent.PaymentMethodCaptured:
      return "Payment Method Captured";
    case WorkflowEvent.PaymentMethodCaptureFailed:
      return "Payment Method Capture Failed";
    case WorkflowEvent.PaymentMethodRemoved:
      return "Payment Method Removed";
    default:
      return "Unknown";
  }
};

const getActionLabel = (action: WorkflowAction) => {
  switch (action) {
    case WorkflowAction.ReadData:
      return "Read Data";
    case WorkflowAction.CreateData:
      return "Create Data";
    case WorkflowAction.UpdateData:
      return "Update Data";
    case WorkflowAction.DeleteData:
      return "Delete Data";
    case WorkflowAction.CallAnApi:
      return "Call an API";
    case WorkflowAction.RunScript:
      return "Run a Script";
    case WorkflowAction.SendACommand:
      return "Send a Command";
    case WorkflowAction.Condition:
      return "Condition";
    case WorkflowAction.SendAnEmail:
      return "Send an Email";
    case WorkflowAction.SendPushNotification:
      return "Send a Push Notification";
    case WorkflowAction.AdjustBalance:
      return "Adjust a Balance";
    case WorkflowAction.Integration:
      return "Integration";
    case WorkflowAction.FileHandler:
      return "File Handler";
    case WorkflowAction.UpsertData:
      return "Upsert Data";
    default:
      return "Unknown";
  }
};

export { getTriggerLabel, getEventLabel, getActionLabel, getStepLoopDescription };
